'use client';

import { useState } from 'react';
import styled, { css } from 'styled-components';
import { filterXSS } from 'xss';
import { useTranslation } from '@/app/components/translations/Translation';
import { HotelPageContentSection } from '@/app/components/hotel-detail/HotelPageContentSection';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { DeprecatedLink as Link, Text } from '@lmn/design-system';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { TextBase } from '@/app/components/hotel-detail/common/text';
import { getWeightFromKg } from '@/app/common/measures/getters.ts';
import {
  EvoModalSectionContainer,
  EvoSectionContainer,
  EvoSectionHeading,
} from '@/app/components/hotel-detail/evo/EvoCommon';
import { getSustainabilityInfoLink } from '@/app/common/sustainability.ts';
import { applyImageTransformation } from '@/app/components/search/utils/images.ts';
import { Icon } from '@/app/common/icons/Icon';
import { useIsLgOrGreater } from './hooks/useMediaSSR';
import { useHotelEmissions } from './hooks/useHotelEmissions';
import { useSustainabilityCertifications } from './hooks/useSustainabilityCertifications';

const CERTIFICATIONS_TEXT_KEYS = {
  singular: 'vacation.select.sustainability.certifications.text.singular',
  plural: 'vacation.select.sustainability.certifications.text.plural',
};

// The section covers the CO2 estimate and the certifications, so it is headed "Sustainability"
// rather than by emissions alone. That copy is still rolling out across markets, so the emissions
// heading — translated everywhere — stands in until each market has the new one, but only while the
// section actually shows an estimate: titling a certifications-only section for emissions it does
// not contain is worse than leaving it untitled until the copy lands.
const CARD_ROW_QUERY = 'sustainability-section';
const THREE_COLUMN_MIN_WIDTH = '720px';
const TWO_COLUMN_MIN_WIDTH = '480px';

const SECTION_TITLE_KEY = 'vacation.select.section.sustainability.title';
const EMISSIONS_TITLE_KEY = 'vacation.oss.emissions.modal.hotel_heading';

export default function HotelPageEmissionsSection(props: React.HTMLAttributes<HTMLElement>) {
  const { localization } = usePageWrapperData();
  const { t } = useTranslation();
  const isLg = useIsLgOrGreater();

  const hotelEmissions = useHotelEmissions();

  return hotelEmissions && hotelEmissions.co2 ? (
    <HotelPageContentSection
      {...props}
      title={t('vacation.oss.emissions.modal.hotel_heading')}
      css={css`
        display: flex;
        flex-direction: column;
        background-color: white;

        @media ${customCss.breakpoints.sm} {
          background-color: inherit;
        }
      `}
    >
      <SectionContent className="hotel-emissions-container">
        <EmissionsContainer>
          {/* TODO German suns */}
          <EmissionLabel
            css={`
              @media ${customCss.breakpoints.xl} {
                &::after {
                  content: ':';
                }
              }
            `}
          >
            {getStarLabel(hotelEmissions.stars, t)}
          </EmissionLabel>
          <EmissionLabel>
            {t('vacation.oss.emissions.modal.CO2', {
              value: `${getWeightFromKg(hotelEmissions.co2, localization).formatted}`,
            })}
          </EmissionLabel>
        </EmissionsContainer>
        <TextBase
          css={`
            flex: 1;
          `}
          dangerouslySetInnerHTML={{
            __html: filterXSS(t('vacation.oss.emissions.modal.hotel_footer').replaceAll('<br />', ' ')),
          }}
        />
        {/* A multicolour illustration, not a glyph: Starfish's monochrome earth is no substitute. */}
        {isLg && <img src={`${localization.imageBasePath}icons/planet-heart.svg`} width={60} height={60} alt="" />}
      </SectionContent>
    </HotelPageContentSection>
  ) : undefined;
}

type CertificationsContent = {
  certifications: SustainabilityCertification[];
  text: string;
  linkLabel: string;
  infoLink: string;
};

/**
 * The certifications block's data and copy, or undefined when there is nothing to show. The
 * introductory sentence is market-managed copy: without it the logos would stand unexplained, so
 * the block waits for the dictionary rather than rendering bare cards.
 */
function useCertificationsContent(): CertificationsContent | undefined {
  const { localization } = usePageWrapperData();
  const { t, translateBaseOnCount } = useTranslation();
  const certifications = useSustainabilityCertifications();

  // The published copy interpolates __number__ (the singular form spells the 1 out, so only the
  // plural actually consumes it).
  const text = certifications.length
    ? translateBaseOnCount(CERTIFICATIONS_TEXT_KEYS, certifications.length, localization.locale, {
        number: `${certifications.length}`,
      })
    : '';
  if (!text) return undefined;

  return {
    certifications,
    text,
    linkLabel: t('vacation.select.sustainability.certifications.link'),
    infoLink: getSustainabilityInfoLink(localization),
  };
}

/**
 * Whether the evo sustainability section has anything to show — the CO2 estimate, the property's
 * certifications, or both. Callers that wrap the section (its divider, the details modal) share
 * this so they can never render a separator above an empty section.
 */
export function useHasSustainabilitySection(): boolean {
  const hotelEmissions = useHotelEmissions();
  const certificationsContent = useCertificationsContent();

  return !!hotelEmissions?.co2 || !!certificationsContent;
}

// The card rows size their columns against the section rather than the viewport, so the query
// container has to sit on whichever container the section is rendered in.
const cardRowContainer = css`
  container: ${CARD_ROW_QUERY} / inline-size;
`;

// EvoSectionContainer's 24px gap is the desktop figure; the mobile frame keeps title -> block and
// block -> block at 16. Overridden here rather than in EvoCommon so the other evo sections keep 24.
const SustainabilitySectionContainer = styled(EvoSectionContainer)`
  ${cardRowContainer}
  gap: 16px;

  @media ${customCss.breakpoints.sm} {
    gap: 24px;
  }
`;

// As a modal tab block the gap follows the tab's own rhythm (EvoModalSectionContainer), so this
// variant only adds the query container.
const SustainabilityModalSectionContainer = styled(EvoModalSectionContainer)`
  ${cardRowContainer}
`;

// `inModal` renders the section as a tab block of the Evo hotel-info modal: smaller heading and
// the tab's title-to-content rhythm. The hotel page itself leaves it unset (MPH-839).
export function HotelPageEmissionsSectionEvo({
  inModal = false,
  ...props
}: React.HTMLAttributes<HTMLElement> & { inModal?: boolean }) {
  const { localization } = usePageWrapperData();
  const { t } = useTranslation();
  const hotelEmissions = useHotelEmissions();
  const certificationsContent = useCertificationsContent();
  const SectionContainer = inModal ? SustainabilityModalSectionContainer : SustainabilitySectionContainer;

  const hasEmissions = !!hotelEmissions?.co2;
  if (!hasEmissions && !certificationsContent) return undefined;

  // Both rows share a column count, which is what keeps the CO2 card lined up with the first
  // certification. Three is the design's row, but only once there are three cards to fill it:
  // fewer would leave the rest of the row empty. Two is also what the estimate falls back to when
  // there are no certifications at all — the flag off, or a property without any — where it lands
  // within ~10px of the width the estimate had before this section was rebuilt.
  const cardColumns = (certificationsContent?.certifications.length ?? 0) >= 3 ? 3 : 2;

  const sectionTitle = t(SECTION_TITLE_KEY) || (hasEmissions ? t(EMISSIONS_TITLE_KEY) : '');

  return (
    <SectionContainer {...props}>
      {sectionTitle ? <EvoSectionHeading $inModal={inModal}>{sectionTitle}</EvoSectionHeading> : undefined}
      {certificationsContent ? (
        <SustainabilityCertificationsEvo {...certificationsContent} columns={cardColumns} />
      ) : undefined}
      {hotelEmissions?.co2 ? (
        <EvoSectionBlock className="hotel-emissions-container">
          <Text
            variant="Body"
            dangerouslySetInnerHTML={{
              __html: filterXSS(t('vacation.oss.emissions.modal.hotel_footer').replaceAll('<br />', ' ')),
            }}
          />
          <EvoCardGrid $columns={cardColumns}>
            <EvoCard>
              <EvoEmissionsIconContainer>
                <Icon category="Services" name="icon-leaf" size={24} color="iconPrimary" />
              </EvoEmissionsIconContainer>
              <EvoEmissionsLabelContainer>
                <Text variant="Title4" forceTag="span">
                  {t('vacation.oss.emissions.modal.CO2', {
                    value: `${getWeightFromKg(hotelEmissions.co2, localization).formatted}`,
                  })}
                </Text>
                <Text variant="Body" color="textSecondary">
                  {getStarLabel(hotelEmissions.stars, t)}
                </Text>
              </EvoEmissionsLabelContainer>
            </EvoCard>
          </EvoCardGrid>
        </EvoSectionBlock>
      ) : undefined}
    </SectionContainer>
  );
}

function SustainabilityCertificationsEvo({
  certifications,
  text,
  linkLabel,
  infoLink,
  columns: columnCount,
}: CertificationsContent & { columns: number }) {
  return (
    <EvoSectionBlock className="hotel-certifications-container">
      <Text variant="Body">
        {text}
        {infoLink && linkLabel ? (
          <>
            {' '}
            <Link
              variant="inline"
              appearance="underlined"
              href={infoLink}
              target="_blank"
              rel="noreferrer"
              css={css`
                ${({ theme }) => theme.kallisto.mixins.font('Body')};
              `}
            >
              {linkLabel}
            </Link>
          </>
        ) : undefined}
      </Text>
      <EvoCardGrid $columns={columnCount}>
        {certifications.map((certification) => (
          <EvoCertificationCard key={certification.name} certification={certification} />
        ))}
      </EvoCardGrid>
    </EvoSectionBlock>
  );
}

// The logos arrive with Cloudinary's `w_auto`, which needs client hints we do not send, so the
// original (measured at 1182px square) is served for a 56px box. Pin it to twice the box instead.
const LOGO_TRANSFORMATION = 'w_112';

function EvoCertificationCard({ certification }: { certification: SustainabilityCertification }) {
  // Logos are third-party CDN assets. A miss keeps the slot — so the names across a row stay
  // aligned — and drops only the browser's broken-image glyph.
  const [logoFailed, setLogoFailed] = useState(false);

  // A logo already requested before React attached onError reports its miss as "loaded with no
  // intrinsic width" instead, and would otherwise keep the broken glyph forever (MPH-618).
  const checkOnMount = (image: HTMLImageElement | null) => {
    if (image?.complete && image.naturalWidth === 0) setLogoFailed(true);
  };

  return (
    <EvoCard>
      <EvoCertificationLogo
        src={applyImageTransformation(certification.logoUrl, LOGO_TRANSFORMATION)}
        alt=""
        width={56}
        height={56}
        loading="lazy"
        $failed={logoFailed}
        ref={checkOnMount}
        onError={() => setLogoFailed(true)}
      />
      <EvoCertificationName>{certification.name}</EvoCertificationName>
    </EvoCard>
  );
}

function getStarLabel(stars: number | null | undefined, t: (key: string) => string): string {
  if (stars === 3) return t('vacation.oss.emissions.modal.hotel_3_star');
  if (stars === 4) return t('vacation.oss.emissions.modal.hotel_4_star');
  if (stars === 5) return t('vacation.oss.emissions.modal.hotel_5_star');
  return t('vacation.oss.emissions.modal.hotel_0_2_star');
}

const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  @media ${customCss.breakpoints.sm} {
    background-color: white;
    padding: 24px;
    border-radius: 8px;
  }

  @media ${customCss.breakpoints.lg} {
    flex-direction: row-reverse;
    gap: 24px;
  }
`;

export const EmissionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${(p) => p.theme.commons.colors.gray50};
  padding: 8px 16px;
  border-radius: 8px;
  gap: 8px;
  width: 100%;

  @media ${customCss.breakpoints.lg} {
    justify-content: center;
    padding: 12px 16px;
    min-width: 290px;
    flex-shrink: 0;
    width: auto;
  }
`;

const EmissionLabel = styled(Text).attrs({ variant: 'BodySmall', weight: 'bold' })`
  white-space: nowrap;
`;

/** An intro paragraph plus its row of cards — the emissions estimate and the certifications. */
const EvoSectionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media ${customCss.breakpoints.sm} {
    gap: 16px;
  }
`;

/**
 * The card row, as a grid so the CO2 estimate and the certifications sit in the same columns.
 *
 * The tiers are container queries, not media queries, because the section's own width is what
 * decides how many cards fit — and it is not monotonic in the viewport: the main column grows to
 * 975px just below `lg`, then the layout's `auto minmax(0, 737px) 423px auto` grid drops it to
 * 553px at `lg` before it climbs back to 737px. Viewport breakpoints would have to go 3 -> 2 -> 3
 * to track that; the container reads the width directly, and works unchanged inside the 961px
 * details modal.
 *
 * 720px is where three cards clear ~230px each ((720 - 32) / 3 = 229), and 480px is the same
 * threshold for two. Below that a card is narrower than its own logo plus a readable name, so the
 * row goes to one.
 */
const columns = (count: number) => css`
  grid-template-columns: repeat(${count}, minmax(0, 1fr));
`;

const EvoCardGrid = styled.div<{ $columns: number }>`
  display: grid;
  gap: 8px;
  ${columns(1)}

  @container ${CARD_ROW_QUERY} (min-width: ${TWO_COLUMN_MIN_WIDTH}) {
    gap: 16px;
    ${(p) => columns(Math.min(p.$columns, 2))}
  }

  @container ${CARD_ROW_QUERY} (min-width: ${THREE_COLUMN_MIN_WIDTH}) {
    ${(p) => columns(Math.min(p.$columns, 3))}
  }
`;

const EvoCard = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border: 1px solid ${(p) => p.theme.kallisto.color.border.static.borderDisabled};
  border-radius: ${(p) => p.theme.kallisto.border.radius.radius200};
  background-color: ${(p) => p.theme.kallisto.color.background.static.backgroundPage};

  @media ${customCss.breakpoints.sm} {
    padding: 16px 24px;
  }
`;

const EvoEmissionsIconContainer = styled.div`
  background-color: ${(p) => p.theme.kallisto.color.background.static.backgroundSuccessSubtle};
  height: 56px;
  width: 56px;
  border-radius: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

// Certification logos are third-party assets of any aspect ratio, so they are fitted inside the
// 56px box rather than cropped to it.
const EvoCertificationLogo = styled.img<{ $failed: boolean }>`
  width: 56px;
  height: 56px;
  flex-shrink: 0;
  object-fit: contain;
  visibility: ${(p) => (p.$failed ? 'hidden' : 'visible')};
`;

// The name is a label, not a section heading, so it keeps Title5's type without its <h5>.
// min-width lets it wrap inside the card instead of stretching it, and overflow-wrap breaks the
// single long compound word that has no space to wrap at.
const EvoCertificationName = styled(Text).attrs({ variant: 'Title5' as const, forceTag: 'span' as const })`
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const EvoEmissionsLabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;
